import { Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useGaPageview } from './hooks/useGaPageview'
import CookieConsent from './components/CookieConsent'
import ScrollToHash from './components/ScrollToHash'
import { ROUTES, REDIRECTS } from './routes'

// ── Marketing pages — one chunk each. ─────────────────────────────────────────
// This build IS the marketing site and nothing else, so there is no
// dashboard/admin chunk to split away and no hostname gate to apply. (V1 shipped
// ONE bundle to three origins and filtered the route set at runtime via
// lib/siteMode.js; V2's dashboard + admin are separate apps, so that machinery is
// gone.)
//
// What IS worth splitting is the pages themselves. An earlier comment here said
// "every page is small and part of the same first-paint story, so lazy-loading
// would only add a round-trip". That was wrong by about 105 KB: LegalDocument.jsx
// statically imports all four legal documents (practiceTerms 41 KB,
// workerPrivacy 45 KB, practicePrivacy 15 KB, practiceCookies 4 KB of raw
// prose), so somebody reading the homepage was downloading the full Terms of
// Business. First-load JS was 150 KB gzipped against the 120 KB budget in
// docs/MARKETING-SITE-SPEC.md §7.3.
//
// So `ROUTES[].Component` is now a code split gate (see routes.jsx). The route
// table itself is still src/routes.jsx, shared with the SSR prerender and the
// sitemap generator. Add a page THERE, not here.
//
// The three things below the router stay EAGER on purpose: they run on every
// route, they are small, and two of them are correctness rather than content.
// ScrollToHash and the GA page_view hook have to be mounted before the first
// route renders, and CookieConsent is a legal obligation that must not wait on a
// second request.

// Fires the GA4 page_view on every route change without re-rendering the
// tree. All consent gating lives in lib/analytics.js.
function TrackingShell() {
  useGaPageview()
  return null
}

// The catch-all 404, taken from the table rather than imported again, so the
// page behind /404 and the page behind an unknown URL can never drift apart.
const NotFound = ROUTES.find((r) => r.path === '/404').Component

// Shown only while a route chunk is in flight on a CLIENT SIDE navigation.
//
// It is deliberately empty rather than a spinner. The first route never reaches
// here — main.jsx resolves that chunk before it mounts, precisely so the
// prerendered markup is not replaced by a fallback (see routes.jsx) — so this
// only ever covers a link click, where the chunks are a few KB and a spinner
// would flash for longer than it reassured anyone. The min-height holds the
// viewport open so the scroll position does not collapse and bounce back, which
// would show up as CLS on a real navigation.
//
// ⛔ Not a "Loading…" string: a route transition is not an event worth narrating,
// and the site has no toasts, spinners or interstitials anywhere else.
function RouteFallback() {
  return <div aria-busy="true" style={{ minHeight: '100vh' }} />
}

export default function App() {
  return (
    <BrowserRouter>
      <TrackingShell />
      <ScrollToHash />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {ROUTES.map(({ path, Component }) => (
            <Route key={path} path={path} element={<Component />} />
          ))}
          {/* The pages the rebuild replaced. Caddy 301s these in production;
              these keep the SPA behaving the same way on a direct hit. */}
          {REDIRECTS.map(({ from, to }) => (
            <Route key={from} path={from} element={<Navigate to={to} replace />} />
          ))}
          {/* ⛔ An unknown path renders the 404, it does NOT redirect home.
              Sending every typo to the homepage at HTTP 200 is a soft 404: the
              visitor is teleported with no idea their link was wrong, and a
              crawler files each bad URL as a duplicate of the homepage. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      {/* ⛔ No exit intent email popup (founder 2026-10-02). ComingSoonPopup
          fired a "join the waitlist" modal after a dwell timer or on exit
          intent, remembered a dismissal for 7 days and nagged returning
          visitors. It is the oldest trick on the internet, it reads as spam,
          and it is the opposite of how the apps treat people: no toasts, no
          interruptions, one clear action in the page itself. If a page needs a
          sign up, the page carries one. Do not reintroduce it. */}
      <CookieConsent />
    </BrowserRouter>
  )
}
